import React, { useEffect, useMemo, useState } from "react";
import MainLayout from "layouts/Main";
import { Grid, Box, Typography, Divider } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import PastForecastsAbsoluteErrorChart from "./PastForecastsAbsoluteErrorChart";
import PastProductionForecastChart from "views/Dashboard/Forecasts/Production/ProductionForecastCharts/PastProductionForecastChart";
import Select from "components/Select";
import ParkSelect from "views/Dashboard/Forecasts/Production/ParkSelect";
import { useAppDispatch, useAppSelector } from "hooks";
import { fetchAllProductionPark } from "store/appSlice";
import { selectProductionParks } from "store/selectors";

const OverviewCharts = () => {
  const dispatch = useAppDispatch();
  const productionParks = useAppSelector(selectProductionParks);

  // Filters for Absolute Error chart
  const [absoluteErrorFilters, setAbsoluteErrorFilters] = useState({
    productionParkType: "",
    productionParkIds: [] as number[],
  });
  const [absoluteErrorSelectedParks, setAbsoluteErrorSelectedParks] = useState<
    { title: string; value: number }[]
  >([]);

  // Filters for Past Production Forecast chart
  const [pastForecastFilters, setPastForecastFilters] = useState({
    productionParkType: "",
    productionParkIds: [] as number[],
  });
  const [pastForecastSelectedParks, setPastForecastSelectedParks] = useState<
    { title: string; value: number }[]
  >([]);
  const [pastForecastDate, setPastForecastDate] = useState<Dayjs | null>(null);

  useEffect(() => {
    dispatch(fetchAllProductionPark(0, 1000, {}));
  }, [dispatch]);

  useEffect(() => {
    const parkIds = absoluteErrorSelectedParks.map((item) => item.value);
    setAbsoluteErrorFilters((prev) => ({
      ...prev,
      productionParkIds: parkIds,
    }));
  }, [absoluteErrorSelectedParks]);

  useEffect(() => {
    const parkIds = pastForecastSelectedParks.map((item) => item.value);
    setPastForecastFilters((prev) => ({ ...prev, productionParkIds: parkIds }));
  }, [pastForecastSelectedParks]);

  const parks = useMemo(
    () =>
      productionParks?.content?.map((park) => ({
        title: park.name,
        value: park.id,
      })) || [],
    [productionParks],
  );

  const absoluteErrorChartFilters = useMemo(
    () => ({
      type: absoluteErrorFilters.productionParkType,
      productionParkType: absoluteErrorFilters.productionParkType,
      productionParkIds: absoluteErrorFilters.productionParkIds,
    }),
    [absoluteErrorFilters.productionParkType, absoluteErrorFilters.productionParkIds],
  );

  const pastForecastChartFilters = useMemo(
    () => ({
      productionParkType: pastForecastFilters.productionParkType,
      productionParkIds: pastForecastFilters.productionParkIds,
    }),
    [pastForecastFilters.productionParkType, pastForecastFilters.productionParkIds],
  );

  return (
    <MainLayout
      tabs={[
        { label: "Summary", path: "OverviewSummary" },
        { label: "Charts", path: "OverviewCharts" },
      ]}
    >
      <Box>
        <Typography variant="body2" color="textSecondary">
          Overview
        </Typography>
        <Typography variant="h5">Charts</Typography>

        <Grid container columnSpacing={4} sx={{ mt: "1rem", mb: "2rem" }}>
          <Grid item xs={6}>
            <Typography
              component="h5"
              variant="h5"
              sx={{ fontSize: "1.2rem", mb: 1 }}
            >
              Absolute error (last 30 days)
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1, "& > *": { width: "100%" } }}>
                <Select
                  label="Park type"
                  id="absoluteErrorParkType"
                  name="absoluteErrorParkType"
                  value={absoluteErrorFilters.productionParkType}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setAbsoluteErrorFilters((prev) => ({
                      ...prev,
                      productionParkType: event.target.value,
                    }))
                  }
                  options={[
                    { label: "All", value: "" },
                    { label: "Solar", value: "SOLAR" },
                    { label: "Wind", value: "WIND" },
                  ]}
                />
              </Box>
              <Box sx={{ flex: 1, "& > *": { width: "100%" } }}>
                <ParkSelect
                  selectedParks={absoluteErrorSelectedParks}
                  setSelectedParks={setAbsoluteErrorSelectedParks}
                  parks={parks}
                  label="Production parks"
                />
              </Box>
            </Box>
            <Box sx={{ minHeight: "300px", width: "100%" }}>
              <PastForecastsAbsoluteErrorChart
                filters={absoluteErrorChartFilters}
              />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Typography
              component="h5"
              variant="h5"
              sx={{ fontSize: "1.2rem", mb: 1 }}
            >
              Past production forecast
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 1.5,
                mb: 1,
              }}
            >
              <Box sx={{ flex: 1, "& > *": { width: "100%" } }}>
                <Select
                  label="Park type"
                  id="pastForecastParkType"
                  name="pastForecastParkType"
                  value={pastForecastFilters.productionParkType}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setPastForecastFilters((prev) => ({
                      ...prev,
                      productionParkType: event.target.value,
                    }))
                  }
                  options={[
                    { label: "All", value: "" },
                    { label: "Solar", value: "SOLAR" },
                    { label: "Wind", value: "WIND" },
                  ]}
                />
              </Box>
              <Box sx={{ mb: 1, "& > *": { width: "100%" } }}>
                <ParkSelect
                  selectedParks={pastForecastSelectedParks}
                  setSelectedParks={setPastForecastSelectedParks}
                  parks={parks}
                  label="Production parks"
                />
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1.5, mb: 1 }}>
              <Box sx={{ flex: 1, mb: 1 }}>
                <DatePicker
                  label="Forecast date"
                  value={pastForecastDate}
                  maxDate={dayjs().add(1, "day")}
                  onChange={(newValue) => setPastForecastDate(newValue)}
                  slotProps={{
                    textField: { size: "small", fullWidth: true },
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ mt: 1, minHeight: "300px", width: "100%" }}>
              <PastProductionForecastChart
                filters={pastForecastChartFilters}
                showTitle={false}
                showDatePicker={false}
                date={pastForecastDate}
                onDateChange={setPastForecastDate}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </MainLayout>
  );
};

export default OverviewCharts;
